import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { backofficeService } from '../../../../application/BackofficeService';
import { Project } from '../../../../domain/backoffice/project/Project';
import useErrorMiddleware from '../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { BackofficeConstants } from '../../context/actions';
import { useBackofficeContext } from '../../context/context';
import { ProjectsTableColumn } from '../../context/tableColumns/projects';

interface UseSearchProjectsProps {
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: string;
}

type UseSearchProjectsResponse = ServiceResponse<Function, PaginatedResult<Project> | undefined> & {
  tableColumns: Array<ProjectsTableColumn>;
};

// Hooks act as 'Adapter' layer.
export const useSearchProjects = ({
  offset,
  limit,
  sortBy,
  sortDirection,
}: UseSearchProjectsProps): UseSearchProjectsResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { backofficeState, backofficeDispatch } = useBackofficeContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [`backoffice_search_projects`, offset, limit, sortBy, sortDirection, backofficeState.projects.predicatesBase64],
    (): Promise<PaginatedResult<Project>> => {
      return backofficeService.searchProjects({
        offset,
        limit,
        sort_by: sortBy,
        sort_direction: sortDirection,
        predicates: backofficeState.projects.predicates,
      });
    },
    {
      retry: false,
      enabled: backofficeState.projects.predicatesBase64 !== `` && backofficeState.projects.predicates.length > 0,
      staleTime: Infinity,
      onSuccess: (projectsPagination) => {
        backofficeDispatch({
          type: BackofficeConstants.SEARCH_PROJECTS_SUCCESS,
          payload: {
            projects: projectsPagination,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Backoffice Retrieve Projects Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  useEffect(() => {
    if (backofficeState.projects.predicates.length > 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backofficeState.projects.predicates]);

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: backofficeState.projects.data,
    tableColumns: backofficeState.projects.tableColumns,
  };
};
